import type { components } from '@/api'
import { exhaustiveGuard } from '@/shared/utils/typeAssertions'
import { get, post, remove } from './client'
import type { BackendMeta } from './types'

type DeleteHub = BackendMeta<'/api/workspaces/{workspace_id}/hubs/{hub_id}', 'delete'>

export async function deleteHub(workspaceId: string, hubId: string) {
  return remove<DeleteHub['queryParams'], DeleteHub['successResponse'], DeleteHub['path']>(
    `/api/workspaces/${workspaceId}/hubs/${hubId}`,
    undefined,
  )
}

type CreateHubPath = `/api/workspaces/${string}/hubs`
type CreateHubResponse = components['schemas']['Hubs.HubResponse']
export type CreateHubRequest = components['schemas']['Hubs.CreateHubRequest']

export function createHub(workspaceId: string, payload: CreateHubRequest) {
  return post<CreateHubRequest, CreateHubResponse, CreateHubPath>(
    `/api/workspaces/${workspaceId}/hubs`,
    payload,
  )
}

export type ListHubsMeta = BackendMeta<'/api/workspaces/{workspace_id}/hubs', 'get'>

export function listHubs(workspaceId: string) {
  return get<ListHubsMeta['queryParams'], ListHubsMeta['successResponse'], ListHubsMeta['path']>(
    `/api/workspaces/${workspaceId}/hubs`,
    undefined,
  )
}

export type GetHubMeta = BackendMeta<'/api/workspaces/{workspace_id}/hubs/{hub_id}', 'get'>

export function getHub(workspaceId: string, hubId: string) {
  return get<GetHubMeta['queryParams'], GetHubMeta['successResponse'], GetHubMeta['path']>(
    `/api/workspaces/${workspaceId}/hubs/${hubId}`,
    undefined,
  )
}

type ReindexHubMeta = BackendMeta<'/api/workspaces/{workspace_id}/hubs/{hub_id}/reindex', 'post'>

export function reindexHub(workspaceId: string, hubId: string) {
  return post<
    ReindexHubMeta['queryParams'],
    ReindexHubMeta['successResponse'],
    ReindexHubMeta['path']
  >(`/api/workspaces/${workspaceId}/hubs/${hubId}/reindex`, undefined)
}

export type AddFilesMeta = BackendMeta<
  '/api/workspaces/{workspace_id}/hubs/{hub_id}/files/add',
  'post'
>
export type AddFilesRequest = components['schemas']['Hubs.AddFilesRequest']
export type AddFilesParams = {
  files: Array<
    | {
        entityId: string
        relativePath: string
        type: 'file'
      }
    | {
        relativePath: string
        type: 'folder'
      }
  >
  parentFolderId?: string | null
}

export function addHubFiles(workspaceId: string, hubId: string, params: AddFilesParams) {
  const files: AddFilesRequest['files'] = []
  for (const file of params.files) {
    switch (file.type) {
      case 'file':
        files.push({
          entity_id: file.entityId,
          relative_path: file.relativePath,
          type: file.type,
        })
        break
      case 'folder':
        files.push({
          relative_path: file.relativePath,
          type: file.type,
        })
        break
      default:
        return exhaustiveGuard(file, 'File entry type not supported')
    }
  }

  const payload: AddFilesRequest = { files, parent_folder_id: params.parentFolderId ?? null }

  return post<AddFilesRequest, AddFilesMeta['successResponse'], AddFilesMeta['path']>(
    `/api/workspaces/${workspaceId}/hubs/${hubId}/files/add`,
    payload,
  )
}

export type MoveFilesMeta = BackendMeta<
  '/api/workspaces/{workspace_id}/hubs/{hub_id}/files/move',
  'post'
>
export type MoveFilesRequest = components['schemas']['Hubs.MoveFilesRequest']
export type MoveFilesParams = { fileIds: string[]; newParentFolderId?: string | null }

export function moveHubFiles(workspaceId: string, hubId: string, params: MoveFilesParams) {
  const payload: MoveFilesRequest = {
    file_ids: params.fileIds,
    new_parent_folder_id: params.newParentFolderId ?? null,
  }

  return post<MoveFilesRequest, MoveFilesMeta['successResponse'], MoveFilesMeta['path']>(
    `/api/workspaces/${workspaceId}/hubs/${hubId}/files/move`,
    payload,
  )
}

export type ListFilesMeta = BackendMeta<'/api/workspaces/{workspace_id}/hubs/{hub_id}/files', 'get'>

export function listHubFiles(workspaceId: string, hubId: string, folderIds?: string[] | null) {
  const params = folderIds ? { folder_ids: folderIds } : {}
  return get<ListFilesMeta['queryParams'], ListFilesMeta['successResponse'], ListFilesMeta['path']>(
    `/api/workspaces/${workspaceId}/hubs/${hubId}/files`,
    params,
  )
}
